import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { db } from 'foodhub-shared/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import type { Tenant } from 'foodhub-shared/types';

/**
 * Tenant Context for admin multi-tenancy support
 * Allows admins to view and switch between tenants
 */

interface TenantContextType {
  selectedTenantId: string | null;
  allTenants: Tenant[];
  currentTenant: Tenant | null;
  loading: boolean;
  error: string | null;
  switchTenant: (tenantId: string) => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const TENANTS_COLLECTION = 'tenants';

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAdminAuth();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [allTenants, setAllTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all tenants when authenticated
  useEffect(() => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setAllTenants([]);
      setSelectedTenantId(null);
      setLoading(false);
      return;
    }

    const fetchTenants = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get all active tenants
        const q = query(
          collection(db, TENANTS_COLLECTION),
          where('status', '==', 'active')
        );
        const querySnapshot = await getDocs(q);
        const tenants = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Tenant[];

        setAllTenants(tenants);

        // Set first tenant as default
        if (tenants.length > 0 && !selectedTenantId) {
          setSelectedTenantId(tenants[0].id);
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching tenants:', err);
        setError(err.message || 'Failed to fetch tenants');
        setLoading(false);
      }
    };

    fetchTenants();
  }, [isAuthenticated]);

  const switchTenant = (tenantId: string) => {
    const tenant = allTenants.find(t => t.id === tenantId);
    if (tenant) {
      setSelectedTenantId(tenantId);
    } else {
      setError(`Tenant ${tenantId} not found`);
    }
  };

  const currentTenant = selectedTenantId
    ? allTenants.find(t => t.id === selectedTenantId) || null
    : null;

  const value: TenantContextType = {
    selectedTenantId,
    allTenants,
    currentTenant,
    loading,
    error,
    switchTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use Tenant Context
 */
export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

